/**
 * Custom hook for managing onboarding state and API calls
 * Provides functions for fetching, updating, and tracking onboarding progress
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { toast } from '@/hooks/use-toast';
import {
  UserOnboardingData,
  OnboardingSection,
  OnboardingStatus,
  SectionProgress,
  OnboardingProgress,
  VALIDATION_RULES,
  SECTION_CONFIGS,
  OnboardingUpdateRequest
} from '@/types/onboarding';

// Query keys for React Query
const QUERY_KEYS = {
  onboarding: (userId: string) => ['onboarding', userId],
  progress: (userId: string) => ['onboarding-progress', userId]
};

export const useOnboarding = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<OnboardingSection>(OnboardingSection.BUSINESS_DETAILS);

  // Fetch onboarding data
  const {
    data: onboardingData,
    isLoading,
    error: fetchError,
    refetch: refetchOnboarding
  } = useQuery({
    queryKey: QUERY_KEYS.onboarding(user?.id || ''),
    queryFn: async (): Promise<UserOnboardingData | null> => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No record found, create initial record
          const newRecord = {
            user_id: user.id,
            organisation_id: user.organisationId,
            onboarding_status: OnboardingStatus.NOT_STARTED,
            business_details_status: OnboardingStatus.NOT_STARTED,
            stripe_connection_status: OnboardingStatus.NOT_STARTED,
            printer_setup_status: OnboardingStatus.NOT_STARTED,
            dns_status: OnboardingStatus.NOT_STARTED,
            website_linked_status: OnboardingStatus.NOT_STARTED,
            google_ordering_status: OnboardingStatus.NOT_STARTED,
            aggregators_setup_status: OnboardingStatus.NOT_STARTED,
            ubereats_setup_status: OnboardingStatus.NOT_STARTED,
            doordash_setup_status: OnboardingStatus.NOT_STARTED,
          };

          const { data: createdData, error: createError } = await supabase
            .from('user_onboarding')
            .insert([newRecord])
            .select()
            .single();

          if (createError) throw createError;
          return createdData;
        }
        throw error;
      }

      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Calculate section progress
  const calculateSectionProgress = useCallback((data: UserOnboardingData): SectionProgress[] => {
    return SECTION_CONFIGS.map(config => {
      const status = (data[config.statusField] as OnboardingStatus) || OnboardingStatus.NOT_STARTED;
      const rules = VALIDATION_RULES[config.id];
      
      // Check if required fields are filled
      const hasRequiredData = rules.required.every(field => {
        const value = data[field];
        return value !== null && value !== undefined && value !== '';
      });

      // Check if any data exists for this section
      const hasAnyData = [...rules.required, ...rules.optional].some(field => {
        const value = data[field];
        return value !== null && value !== undefined && value !== '';
      });

      const validationErrors: string[] = [];
      if (!hasRequiredData) {
        const missingFields = rules.required.filter(field => {
          const value = data[field];
          return value === null || value === undefined || value === '';
        });
        validationErrors.push(`Missing required fields: ${missingFields.join(', ')}`);
      }

      return {
        section: config.id,
        status,
        isRequired: config.isRequired,
        hasData: hasAnyData,
        validationErrors: validationErrors.length > 0 ? validationErrors : undefined
      };
    });
  }, []);

  // Calculate overall progress
  const calculateOverallProgress = useCallback((sections: SectionProgress[]): OnboardingProgress => {
    const requiredSections = sections.filter(s => s.isRequired);
    const completedRequiredSections = requiredSections.filter(s => s.status === OnboardingStatus.COMPLETE);
    const awaitingConfirmation = requiredSections.filter(s => s.status === OnboardingStatus.AWAITING_CONFIRMATION);
    const needsAttention = requiredSections.filter(s => s.status === OnboardingStatus.NEEDS_ATTENTION);

    let overallStatus: OnboardingStatus;
    if (needsAttention.length > 0) {
      overallStatus = OnboardingStatus.NEEDS_ATTENTION;
    } else if (completedRequiredSections.length === requiredSections.length) {
      overallStatus = OnboardingStatus.COMPLETE;
    } else if (awaitingConfirmation.length > 0) {
      overallStatus = OnboardingStatus.AWAITING_CONFIRMATION;
    } else {
      overallStatus = OnboardingStatus.NOT_STARTED;
    }

    const progressPercentage = Math.round(
      (completedRequiredSections.length / requiredSections.length) * 100
    );

    return {
      overallStatus,
      completedSections: completedRequiredSections.length,
      totalSections: requiredSections.length,
      progressPercentage,
      sections
    };
  }, []);

  // Derived progress data
  const progress = onboardingData ? 
    calculateOverallProgress(calculateSectionProgress(onboardingData)) : 
    null;

  // Update section mutation
  const updateSectionMutation = useMutation({
    mutationFn: async ({ section, data }: OnboardingUpdateRequest) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: updatedData, error } = await supabase
        .from('user_onboarding')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return updatedData;
    },
    onSuccess: (data) => {
      // Update the cache
      queryClient.setQueryData(QUERY_KEYS.onboarding(user?.id || ''), data);
      toast({
        title: "Progress Saved",
        description: "Your onboarding progress has been saved successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating onboarding section:', error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Mark section complete mutation
  const markSectionCompleteMutation = useMutation({
    mutationFn: async (section: OnboardingSection) => {
      if (!user?.id || !onboardingData) throw new Error('User not authenticated or no data');

      // Get the status field for this section
      const config = SECTION_CONFIGS.find(c => c.id === section);
      if (!config) throw new Error('Invalid section');

      // Validate section before marking complete
      const sectionProgress = calculateSectionProgress(onboardingData);
      const currentSection = sectionProgress.find(s => s.section === section);
      
      if (currentSection?.validationErrors?.length) {
        throw new Error(`Cannot mark complete: ${currentSection.validationErrors.join(', ')}`);
      }

      const updateData = {
        [config.statusField]: OnboardingStatus.AWAITING_CONFIRMATION,
        updated_at: new Date().toISOString()
      };

      const { data: updatedData, error } = await supabase
        .from('user_onboarding')
        .update(updateData)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return updatedData;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.onboarding(user?.id || ''), data);
      toast({
        title: "Section Completed",
        description: "Section marked as complete and is awaiting admin confirmation.",
      });
    },
    onError: (error) => {
      console.error('Error marking section complete:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to mark section complete.",
        variant: "destructive",
      });
    }
  });

  // Validate section function
  const validateSection = useCallback((section: OnboardingSection): boolean => {
    if (!onboardingData) return false;
    
    const rules = VALIDATION_RULES[section];
    return rules.required.every(field => {
      const value = onboardingData[field];
      return value !== null && value !== undefined && value !== '';
    });
  }, [onboardingData]);

  // Get section status
  const getSectionStatus = useCallback((section: OnboardingSection): OnboardingStatus => {
    if (!onboardingData) return OnboardingStatus.NOT_STARTED;
    
    const config = SECTION_CONFIGS.find(c => c.id === section);
    if (!config) return OnboardingStatus.NOT_STARTED;
    
    return (onboardingData[config.statusField] as OnboardingStatus) || OnboardingStatus.NOT_STARTED;
  }, [onboardingData]);

  // Get section data
  const getSectionData = useCallback((section: OnboardingSection) => {
    if (!onboardingData) return {};
    
    const rules = VALIDATION_RULES[section];
    const sectionData: any = {};
    
    [...rules.required, ...rules.optional].forEach(field => {
      if (onboardingData[field] !== undefined) {
        sectionData[field] = onboardingData[field];
      }
    });
    
    return sectionData;
  }, [onboardingData]);

  // Update section function
  const updateSection = useCallback(async (section: OnboardingSection, data: Partial<UserOnboardingData>) => {
    return updateSectionMutation.mutateAsync({ section, data });
  }, [updateSectionMutation]);

  // Mark section complete function
  const markSectionComplete = useCallback(async (section: OnboardingSection) => {
    return markSectionCompleteMutation.mutateAsync(section);
  }, [markSectionCompleteMutation]);

  // Get restaurant name from onboarding data
  const getRestaurantName = useCallback((): string => {
    return onboardingData?.restaurant_name || onboardingData?.organisation_name || 'Your Restaurant';
  }, [onboardingData]);

  return {
    // Data
    onboardingData,
    progress,
    activeSection,
    
    // Loading states
    isLoading,
    isSaving: updateSectionMutation.isPending,
    isMarkingComplete: markSectionCompleteMutation.isPending,
    
    // Error states
    error: fetchError,
    
    // Actions
    setActiveSection,
    updateSection,
    markSectionComplete,
    refetchOnboarding,
    
    // Utilities
    validateSection,
    getSectionStatus,
    getSectionData,
    getRestaurantName,
    calculateSectionProgress,
    calculateOverallProgress
  };
};